/**
 * The report builder, approval and export.
 *
 * Three things this screen has to make visible, because they are the difference between
 * a defensible document and a plausible one:
 *
 * 1. **Approval is a separate act by a separate role.** Generating costs nothing;
 *    approving is what lets a document leave.
 * 2. **A download can be refused after approval.** Evidence gets erased and hypotheses
 *    get dismissed, and the gate runs at download. A refusal shows its reason rather
 *    than a status code.
 * 3. **What the report rests on** — the two manifests and the claim count — is shown
 *    before anyone approves it.
 */

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, download, request } from '@/lib/api';
import type { GenerateReportRequest, Report, ReportClaim } from '@/lib/schemas';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Loading,
  ProvenanceLink,
  Table,
} from '@/components/ui';
import { useCan } from '@/hooks/usePermissions';

const PERSONAS = [
  { value: 'investigator', label: 'Investigator — the working, not only the conclusion' },
  { value: 'due_diligence', label: 'Due diligence — the finding and its basis' },
];

const FORMATS = ['pdf', 'html', 'markdown', 'json', 'csv'];

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'muted' | 'accent'> = {
  generating: 'muted',
  ready: 'accent',
  approved: 'ok',
  evidence_revoked: 'danger',
  hypothesis_regressed: 'danger',
  failed: 'danger',
};

/** The reports tab for one case. */
export function ReportsTab({ caseId }: { caseId: string }): ReactElement {
  const canGenerate = useCan('generate_reports');
  const queryClient = useQueryClient();
  const [persona, setPersona] = useState(PERSONAS[0]!.value);
  const [format, setFormat] = useState('pdf');

  const reports = useQuery({
    queryKey: ['reports', caseId],
    queryFn: () => request<Report[]>(`/cases/${caseId}/reports`),
  });

  const generate = useMutation({
    mutationFn: () =>
      request<Report, GenerateReportRequest>(`/cases/${caseId}/reports`, {
        method: 'POST',
        body: { persona, format },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reports', caseId] }),
  });

  if (reports.isLoading) return <Loading />;
  if (reports.error) return <ErrorState error={reports.error} />;

  return (
    <div className="space-y-4">
      <CaseExport caseId={caseId} />

      {canGenerate && (
        <Card title="Generate a report">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block text-sm">
              <span className="text-muted">Persona</span>
              <select
                value={persona}
                onChange={(event) => setPersona(event.target.value)}
                className="mt-1 w-96 rounded border border-line bg-surface px-2 py-1.5 text-sm"
              >
                {PERSONAS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-muted">Format</span>
              <select
                value={format}
                onChange={(event) => setFormat(event.target.value)}
                className="mt-1 rounded border border-line bg-surface px-2 py-1.5 text-sm"
              >
                {FORMATS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <Button tone="primary" disabled={generate.isPending} onClick={() => generate.mutate()}>
              {generate.isPending ? 'Generating…' : 'Generate'}
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted">
            Only confirmed hypotheses are included. Unconfirmed ones are not written into the
            document at all, rather than written and withheld.
          </p>
          {generate.error != null && (
            <div className="mt-3">
              <ErrorState error={generate.error} />
            </div>
          )}
        </Card>
      )}

      {(reports.data ?? []).length === 0 ? (
        <p className="text-sm text-muted">No reports have been generated for this case.</p>
      ) : (
        (reports.data ?? []).map((report) => (
          <ReportCard key={report.id} caseId={caseId} report={report} />
        ))
      )}
    </div>
  );
}

/** One report, with what it rests on and what can be done with it. */
function ReportCard({ caseId, report }: { caseId: string; report: Report }): ReactElement {
  const canApprove = useCan('approve_export_reports');
  const queryClient = useQueryClient();
  const [refusal, setRefusal] = useState<{ code: string; message: string } | null>(null);
  const [showClaims, setShowClaims] = useState(false);

  const approve = useMutation({
    mutationFn: () => request<Report>(`/reports/${report.id}:approve`, { method: 'POST' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['reports', caseId] }),
  });

  const claims = useQuery({
    queryKey: ['report-claims', report.id],
    queryFn: () => request<ReportClaim[]>(`/reports/${report.id}/claims`),
    enabled: showClaims,
  });

  async function onDownload(): Promise<void> {
    setRefusal(null);
    try {
      await download(
        `/reports/${report.id}/download`,
        `${report.id}.${report.format === 'markdown' ? 'md' : report.format}`,
      );
    } catch (caught) {
      if (!(caught instanceof ApiError)) throw caught;

      // A refusal is not an error in the platform. It says the document can no longer be
      // supported by the evidence behind it, and the reader needs to know which.
      //
      // **Read from two shapes, deliberately.** A refusal is an `HTTPException` whose
      // `detail` is an object; every other failure is a problem document whose `detail`
      // is a string and whose `code` sits at the top level. Reading only the first shape
      // turned an unauthenticated request into "REFUSED — This report cannot be issued.",
      // which named the export gate for something it had not done.
      const body = caught.detail as
        | { detail?: { code?: string; message?: string } | string; code?: string }
        | null;
      const nested = body !== null && typeof body?.detail === 'object' ? body.detail : null;

      setRefusal({
        code: nested?.code ?? caught.code ?? 'REFUSED',
        message: nested?.message ?? caught.message,
      });
      void queryClient.invalidateQueries({ queryKey: ['reports', caseId] });
    }
  }

  return (
    <Card
      title={`${report.persona} · ${report.format}`}
      action={<Badge tone={STATUS_TONE[report.status] ?? 'muted'}>{report.status}</Badge>}
    >
      <div className="space-y-3">
        <dl className="grid gap-2 text-xs sm:grid-cols-4">
          <div>
            <dt className="uppercase text-muted">Claims</dt>
            <dd className="tabular-nums">{report.claim_count}</dd>
          </div>
          <div>
            <dt className="uppercase text-muted">Evidence cited</dt>
            <dd className="tabular-nums">{report.evidence_manifest.length}</dd>
          </div>
          <div>
            <dt className="uppercase text-muted">Hypotheses included</dt>
            <dd className="tabular-nums">{report.hypothesis_manifest.length}</dd>
          </div>
          <div>
            <dt className="uppercase text-muted">Template</dt>
            <dd className="font-mono">{report.template_version}</dd>
          </div>
        </dl>

        {report.revocation_reason != null && (
          <p className="rounded border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            {report.revocation_reason}
          </p>
        )}

        {refusal != null && (
          <p
            role="alert"
            className="rounded border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn"
          >
            <strong className="font-mono">{refusal.code}</strong> — {refusal.message}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowClaims((open) => !open)}>
            {showClaims ? 'Hide sentences' : 'Show sentences and citations'}
          </Button>
          {report.status === 'ready' && canApprove && (
            <Button tone="primary" disabled={approve.isPending} onClick={() => approve.mutate()}>
              Approve for issue
            </Button>
          )}
          {report.status === 'approved' && (
            <Button tone="secondary" onClick={() => void onDownload()}>
              ⬇ Download
            </Button>
          )}
        </div>

        {report.status === 'ready' && !canApprove && (
          <p className="text-xs text-muted">
            Approving requires the <code>approve_export_reports</code> permission, which your
            role does not hold.
          </p>
        )}

        {approve.error != null && <ErrorState error={approve.error} />}

        {showClaims && (
          <div>
            {claims.isLoading && <Loading />}
            {claims.data && (
              <Table
                headers={['Sentence', 'Confidence', 'Evidence']}
                empty="This report contains no sentences."
                rows={claims.data.map((claim) => [
                  claim.text,
                  <span key="c" className="font-mono text-xs">
                    {claim.confidence_display}
                  </span>,
                  <span key="e" className="flex flex-wrap gap-1">
                    {claim.cited_assertion_ids.map((id) => (
                      <ProvenanceLink key={id} caseId={caseId} assertionId={id} compact />
                    ))}
                  </span>,
                ])}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * The whole-case bundle.
 *
 * **Built and unreachable.** `GET /cases/{id}/export` has shipped since M7 and the Reports
 * tab only ever downloaded an individual report, so the bundle could be fetched with
 * `curl` and no other way.
 *
 * It goes through the **same export gate** as a report — finding S15: an export that
 * assembled its own bundle would bypass the check that a document still rests on evidence
 * nobody has revoked. So a refusal here is the gate working, and is shown as such rather
 * than as an error.
 *
 * @param props - The case to export.
 * @returns The control.
 */
function CaseExport({ caseId }: { caseId: string }): ReactElement {
  const [refusal, setRefusal] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function onExport(): Promise<void> {
    setRefusal(null);
    setBusy(true);
    try {
      await download(`/cases/${caseId}/export`, `case-${caseId}.zip`);
    } catch (caught) {
      if (!(caught instanceof ApiError)) throw caught;
      setRefusal(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Export the whole case">
      <div className="space-y-2 p-4">
        <p className="text-xs text-muted">
          Every approved report and the evidence they cite, as one bundle. Subject to the
          same export gate as an individual report — a bundle resting on evidence that has
          since been revoked is refused here rather than assembled quietly.
        </p>
        <Button data-testid="export-case" disabled={busy} onClick={() => void onExport()}>
          {busy ? 'Assembling…' : '⬇ Export case'}
        </Button>
        {refusal != null && <ErrorState error={refusal} />}
      </div>
    </Card>
  );
}
